import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Icon, Text } from '@chakra-ui/react'
import { Base64 } from 'js-base64'
import _ from 'lodash'
import React, { useContext, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { RiDeleteBinLine } from 'react-icons/ri'
import { useHistory } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { useGetQueryParams } from '../../../hooks/useGetQueryParams'
import useToast from '../../../hooks/useToast'
import type {
  DeleteFilesBulkErrorResponse,
  DeleteFilesBulkSuccessResponse,
  DeleteFolderErrorResponse,
  ViewableParentSuccessResponse,
  FileDetailsResponse,
  MediaLibrarySearchFileResponse,
} from '../../../interfaces'
import { MLContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import type { DeleteMediaCollectionErrorResponse } from '../../../interfaces'

interface Props {
  isOpen: boolean
  onClose(): void
  fileDetails?: FileDetailsResponse
}

const DeleteMediaModal = ({ isOpen, onClose, fileDetails}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  let [
    {
      selectedFiles,
      selectedFolders,
      selectedBreadcrumbFolder,
      mediaCollectionRoute,
      currentMediaCollection,
      viewableParent,
      selectedMediaCollections,
      selectedBreadcrumbMediaCollection,
    },
    dispatch,
  ] = useContext(MLContext)

  if(fileDetails){
    selectedFiles = [fileDetails as unknown as MediaLibrarySearchFileResponse];
    selectedFolders = [];
    selectedMediaCollections = [];
    selectedBreadcrumbFolder = undefined;
    selectedBreadcrumbMediaCollection = undefined;
  }

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const history = useHistory()

  const queryFromUrl = useGetQueryParams()

  const deleteMedia = async (): Promise<void | boolean> => {
    let isBreadcrumbFolderDeleted = false
    let isBreadcrumbCollectionDeleted = false
    const deletedCollectionsStatus: number[] = []
    const removedFileIds: string[] = []
    const removedFolderIds: string[] = []
    const removedMediaCollectionIds: string[] = []

    const isFileFolderSelected =
      selectedFiles.length > 0 || selectedFolders.length > 0

    if (selectedFiles.length > 0) {
      const fileIds = selectedFiles.map(file => file.fileId)
      const res = (await request.post('/api/v2/files/batch/deleteByFileIds', {
        fileIds,
      })) as DeleteFilesBulkSuccessResponse | DeleteFilesBulkErrorResponse

      if (request.error) {
        if ([400, 404].includes(response.status)) {
          toast({
            description: (res as DeleteFilesBulkErrorResponse).message,
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        } else {
          toast({
            description: t(
              'error_in_deleting_files',
              'Error in deleting file(s), please try again after sometime',
            ),
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        }
      } else {
        const okRes = res as DeleteFilesBulkSuccessResponse
        removedFileIds.push(...(okRes.successfullyDeletedFileIds ?? []))

        if (response.status === 207) {
          const count = okRes.successfullyDeletedFileIds.length
          toast({
            description:
              count !== 0 ? (
                <Trans t={t} i18nKey="file_deleted_partially" count={count}>
                  {{ count }} file(s) deleted successfully. {okRes.message}
                </Trans>
              ) : (
                t(
                  'no_file_deleted',
                  'No file was deleted. Check response for more details.',
                )
              ),
            status: count === 0 ? 'error' : 'info',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        } else {
          const count = okRes.successfullyDeletedFileIds.length
          toast({
            description: (
              <Trans t={t} i18nKey="file_deleted_successfully" count={count}>
                {{ count }} file(s) deleted successfully
              </Trans>
            ),
            status: 'success',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        }
      }
    }

    if (selectedFolders.length > 0) {
      const deletedFoldersStatus: number[] = []
      const folderPaths = selectedFolders.map(folder => folder.folderPath)

      for await (const folderPath of folderPaths) {
        const res = (await request.delete('/api/v2/folder/', {
          folderPath,
        })) as null | DeleteFolderErrorResponse

        if (request.error) {
          if ([400, 404].includes(response.status)) {
            toast({
              description: res?.message,
              status: 'error',
              isClosable: true,
              duration: 5000,
              position: 'top-right',
              variant: 'outline',
            })
          } else {
            toast({
              description: (
                <Trans t={t} i18nKey="error_in_deleting_folder">
                  Error in deleting folder {{ folderPath }}, please try again
                  after sometime
                </Trans>
              ),
              status: 'error',
              isClosable: true,
              duration: 5000,
              position: 'top-right',
              variant: 'outline',
            })
          }
        } else {
          if (selectedBreadcrumbFolder?.folderPath === folderPath) {
            isBreadcrumbFolderDeleted = true
          }
          const f = selectedFolders.find(sf => sf.folderPath === folderPath)
          if (f?.folderId) {
            removedFolderIds.push(f.folderId)
          }
          deletedFoldersStatus.push(response.status)
        }
      }

      if (deletedFoldersStatus.length > 0) {
        const count = deletedFoldersStatus.length
        toast({
          description: (
            <Trans t={t} i18nKey="folder_deleted_successfully" count={count}>
              {{ count }} folder(s) deleted successfully
            </Trans>
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })

        if (isBreadcrumbFolderDeleted) {
          let folderPath = selectedBreadcrumbFolder?.folderPath as string
          let viewableFolderPath = folderPath
          let unViewableBasePath = ''
          let newFolderPath = ''

          if (
            viewableParent &&
            !_.isUndefined(
              (viewableParent as ViewableParentSuccessResponse).asset,
            )
          ) {
            const index = (
              viewableParent as ViewableParentSuccessResponse
            ).asset?.assetPath.lastIndexOf('/')
            if (index) {
              viewableFolderPath = folderPath.slice(index, folderPath.length)
              unViewableBasePath = folderPath.slice(0, index)
            }
          }

          const folderPathArray = viewableFolderPath.split('/')
          if (
            folderPathArray[folderPathArray.length - 1] ===
            selectedBreadcrumbFolder?.name
          ) {
            folderPathArray.pop()
          }
          viewableFolderPath = folderPathArray.join('/')

          if (viewableFolderPath && viewableFolderPath !== '/') {
            newFolderPath = `${unViewableBasePath}${viewableFolderPath}`
          }
          mediaCollectionRoute
            ? history.replace(
                `/media-collections/${currentMediaCollection}/${Base64.encode(
                  newFolderPath,
                  true,
                )}${queryFromUrl as string}`,
              )
            : history.replace(
                `/media-library/${Base64.encode(newFolderPath, true)}${
                  queryFromUrl as string
                }`,
              )
        }
      }
    }

    for await (const mediaCollection of selectedMediaCollections) {
      const res = (await request.delete(
        `/api/v2/media-collections/${mediaCollection.mediaCollectionId}`,
      )) as null | DeleteMediaCollectionErrorResponse

      if (request.error) {
        const collectionName = mediaCollection.name
        toast({
          description: res?.message ?? (
            <Trans t={t} i18nKey="error_in_deleting_media_collection">
              Error in deleting media collection {{ collectionName }}, please
              try again after sometime.
            </Trans>
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        if (
          selectedBreadcrumbMediaCollection?.mediaCollectionId ===
          mediaCollection.mediaCollectionId
        ) {
          isBreadcrumbCollectionDeleted = true
        }
        removedMediaCollectionIds.push(mediaCollection.mediaCollectionId)
        deletedCollectionsStatus.push(response.status)
      }
    }

    if (removedFileIds.length > 0) {
      dispatch({
        type: MediaLibraryActions.REMOVE_FILES,
        payload: removedFileIds,
      })
    }
    if (removedFolderIds.length > 0) {
      dispatch({
        type: MediaLibraryActions.REMOVE_FOLDERS,
        payload: removedFolderIds,
      })
    }
    if (removedMediaCollectionIds.length > 0) {
      dispatch({
        type: MediaLibraryActions.REMOVE_MEDIA_COLLECTIONS,
        payload: removedMediaCollectionIds,
      })
    }

    if (removedMediaCollectionIds.length) {
      dispatch({ type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS })
    }
    if (removedFileIds.length || removedFolderIds.length) {
      dispatch({ type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS })
    }

    if (deletedCollectionsStatus.length > 0) {
      const count = deletedCollectionsStatus.length
      toast({
        description: (
          <Trans
            t={t}
            i18nKey="media_collections_deleted_successfully"
            count={count}
          >
            {{ count }} media collection(s) deleted successfully
          </Trans>
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })

      window.trackEvent({
        action: 'Submit',
        category: 'Media collection',
        label: 'Delete',
        value: count,
      })
    }
    dispatch({
      type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
    })

    if (isBreadcrumbCollectionDeleted && !isFileFolderSelected) {
      history.replace('/media-collections')
    }

    return isBreadcrumbFolderDeleted
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Icon
            as={RiDeleteBinLine}
            color="appBattleshipGrey"
            backgroundColor="appPaleGrey"
            boxSize={6}
            padding="0.1em"
            borderRadius="full"
            marginRight="0.5em"
          />
          {t('delete_assets', 'Delete Assets')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color="appBattleshipGrey">
            {t(
              'delete_asset_description',
              `This will permanently delete all the selected files, folders (including all content within any selected folders) and selected media collections. Deleting a media collection will also disassociate all linked assets, users, and user groups. Once submitted, this action cannot be undone.`,
            )}
          </Text>
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button onClick={onClose} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            colorScheme="appRed"
            isDisabled={isSubmitting}
            onClick={async () => {
              setSubmitting(true)
              const isBreadcrumbFolderDeleted = await deleteMedia()
              setSubmitting(false)
              onClose()
            }}
          >
            {t('submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DeleteMediaModal
